// src/templates/empanadaclub/TicketView.jsx
// Vista personalizada de ticket para Empanada Club
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { get, getBlob } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import QRCode from 'qrcode';
import heroImage from '../../assets/heroEmpanadaClub.png';

const LoadingSpinner = () => (
    <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
            <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <div className="text-xl text-gray-700 font-medium">Cargando tu ticket...</div>
            <p className="text-gray-500 mt-2">Espera un momento por favor</p>
        </div>
    </div>
);

const ErrorView = () => (
    <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center max-w-md mx-auto p-6 bg-white rounded-xl shadow-lg">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </div>
            <div className="text-xl text-red-600 font-semibold mb-2">Ticket no encontrado</div>
            <p className="text-gray-600">Lo sentimos, el ticket que buscas no existe o ha sido eliminado</p>
        </div>
    </div>
);

const TicketView = () => {
    const { ticketUrl } = useParams();
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');

    // URL actual del ticket para redirecciones
    const currentTicketUrl = window.location.href;

    useEffect(() => {
        const fetchTicket = async () => {
            try {
                if (!ticketUrl) {
                    throw new Error('URL del ticket no proporcionada');
                }

                const response = await get(`tickets/url/${ticketUrl}`);
                setTicket(response);
            } catch (error) {
                console.group('❌ Error Details');
                console.error('Error Message:', error.message);
                console.error('Full Error:', error);
                console.groupEnd();

                Swal.fire({
                    title: 'Error',
                    text: error.message || 'No se pudo cargar el ticket',
                    icon: 'error',
                    confirmButtonText: 'Aceptar',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchTicket();
    }, [ticketUrl]);

    // Obtener QR del ticket cuando esté cargado
    useEffect(() => {
        const getTicketQR = async () => {
            if (!ticket) return;

            try {
                // Crear URL del endpoint para TPV
                const baseUrl = window.location.origin;
                const qrEndpointUrl = `${baseUrl}/api/tickets/${ticket._id}/qr-accumulate`;

                console.log('Generando QR con URL:', qrEndpointUrl);

                // Generar el QR con la URL del endpoint
                const qrDataUrl = await QRCode.toDataURL(qrEndpointUrl, {
                    width: 200,
                    margin: 2,
                    color: {
                        dark: '#000000',
                        light: '#FFFFFF'
                    }
                });

                console.log('QR generado exitosamente:', qrDataUrl.substring(0, 50) + '...');
                setQrCodeDataUrl(qrDataUrl);
            } catch (error) {
                console.error('Error generando QR del ticket:', error);
            }
        };

        getTicketQR();
    }, [ticket]);

    if (loading) return <LoadingSpinner />;
    if (!ticket) return <ErrorView />;

    const accumulatedPoints = ticket.puntos || 0;
    const clienteNombre = `${ticket.cliente?.nombre || ''} ${ticket.cliente?.apellido || ''}`.trim() || 'Usuario';
    const marcaNombre = ticket.marca?.nombre || 'Empanada Club';
    const marcaEslogan = ticket.marca?.eslogan || '';

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md mx-auto">
                {/* Card principal - Estilo negro para Empanada Club */}
                <div className="bg-black rounded-[24px] shadow-xl overflow-hidden relative">
                    {/* Header negro con usuario */}
                    <div className="bg-black text-white p-6">
                        <div className="flex items-center justify-end">
                            {/* Usuario y nombre del cliente */}
                            <div className="text-right">
                                <p className="text-sm text-white opacity-80">usuario</p>
                                <p className="text-base font-semibold text-white">{clienteNombre}</p>
                            </div>
                        </div>
                    </div>

                    {/* Hero Image */}
                    <div className="w-full">
                        <img
                            src={heroImage}
                            alt="Empanada Club Hero"
                            className="w-full h-auto object-cover"
                        />
                    </div>

                    {/* Sección de slogan, nombre de marca y puntos */}
                    <div className="bg-black p-6">
                        <div className="flex items-start justify-between mb-6">
                            {/* Izquierda: Slogan y nombre de marca */}
                            <div className="flex-1">
                                {marcaEslogan && (
                                    <p className="text-white text-sm mb-2">{marcaEslogan}</p>
                                )}
                                <h2 className="text-2xl font-bold text-white">{marcaNombre}</h2>
                            </div>

                            {/* Derecha: Empanada Coin y puntos */}
                            <div className="text-right">
                                <p className="text-white text-sm mb-1">empanada coin</p>
                                <p className="text-2xl font-bold text-white">{accumulatedPoints}</p>
                            </div>
                        </div>

                        {/* QR Code centrado */}
                        <div className="flex justify-center">
                            <div className="bg-white p-4 rounded-xl">
                                {qrCodeDataUrl ? (
                                    <img
                                        src={qrCodeDataUrl}
                                        alt="QR Code para acumulación de puntos"
                                        className="w-40 h-40 object-contain"
                                    />
                                ) : (
                                    <div className="w-40 h-40 bg-gray-100 rounded-lg flex items-center justify-center">
                                        <svg className="w-20 h-20 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5zm13-2h3v2h-3v-2zm-3 0h2v2h-2v-2zm3 3h3v3h-3v-3zm-3 0h2v3h-2v-3z" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TicketView;

